/**
 * Sentinel Evaluator - General strategy minimax
 * Simple, straightforward evaluation focused on material and positioning
 */

import { GameState, IPiece, PieceType, State } from "../../types";
import { isRaceVictory, RACE_VICTORY_BONUS } from "./MinimaxUtils";

export interface EvaluationBreakdown {
  lightPieces: Array<{ piece: string; position: string; breakdown: string; score: number; isKing: boolean }>;
  darkPieces: Array<{ piece: string; position: string; breakdown: string; score: number; isKing: boolean }>;
  lightTotal: number;
  darkTotal: number;
  finalScore: number;
  perspective: 'Light' | 'Dark';
}

export class SentinelEvaluator {
  // Store the last evaluation breakdown for UI display
  static lastBreakdown: EvaluationBreakdown | null = null;

  /**
   * Evaluate a position from the perspective of the MAXIMIZING player (the AI)
   * Positive = good for AI, Negative = bad for AI
   *
   * CRITICAL: We must evaluate from the ORIGINAL AI's perspective, NOT gameState.isLightTurn!
   * gameState.isLightTurn flips after each move, so using it would make the AI think
   * capturing its own pieces is good (because perspective flips and enemy looks stronger)
   */
  static evaluate(gameState: GameState, isMaximizing: boolean, debug: boolean = false): number {
    // BUG FIX: Evaluate from MAXIMIZING player's perspective (the AI's original color)
    // NOT from gameState.isLightTurn which flips after each simulated move!
    //
    // When isMaximizing=true: we're evaluating from AI's perspective (wants high score)
    // When isMaximizing=false: we're evaluating from opponent's perspective (AI wants low score)
    // But we ALWAYS score pieces from the same color perspective!
    //
    // The correct perspective is: who was the maximizing player at the ROOT of the search?
    // We need to track this through the search tree. For now, we infer it from the turn:
    // If it's AI's turn in current state AND isMaximizing=true → AI is that color
    // If it's AI's turn in current state AND isMaximizing=false → AI is opposite color
    const aiIsLight = gameState.isLightTurn === isMaximizing;
    const perspective = aiIsLight;

    let score = 0;

    // Detailed breakdown tracking
    const lightPieces: Array<{ piece: string; position: string; breakdown: string; score: number; isKing: boolean }> = [];
    const darkPieces: Array<{ piece: string; position: string; breakdown: string; score: number; isKing: boolean }> = [];
    let lightTotal = 0;
    let darkTotal = 0;

    // Check for terminal positions first
    if (gameState.state === State.PostGame) {
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return perspective ? winScore : -winScore;
      }
      if (gameState.lightWon === false) {
        return perspective ? -winScore : winScore;
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }

    // Evaluate all pieces on the board
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;

        for (const piece of pieces) {
          const isOurs = piece.isLight === perspective;
          const multiplier = isOurs ? 1 : -1;

          let pieceScore = 0;
          let pieceBreakdown: string[] = [];

          // 1. PIECE COUNT: +10/-10 per piece
          score += 10 * multiplier;
          pieceScore += 10;
          pieceBreakdown.push(`piece(+10)`);

          // 2. KING VALUE: +1000/-1000 per king
          if (piece.isKing) {
            score += 1000 * multiplier;
            pieceScore += 1000;
            pieceBreakdown.push(`king(+1000)`);

            // Note: No goal-row bonus needed here - game immediately ends with ±100000 when king reaches goal
            // The terminal state check (lines 52-63) handles victory detection
          }

          // 4. POSITIONAL SCORING: Forward movement rewards
          // Note: Virus doesn't get position bonus (it's meant to infect, not advance)
          const middleLine = 3.5; // Between rows 3 and 4
          let distanceFromStart: number;

          if (piece.isLight) {
            distanceFromStart = row; // Light starts at row 0, advances to row 7
          } else {
            distanceFromStart = 7 - row; // Dark starts at row 7, advances to row 0
          }

          // +2 per square toward middle line (rows 0-3 for light, rows 7-4 for dark)
          // +1 per square past middle line (rows 4-7 for light, rows 3-0 for dark)
          let positionScore = 0;
          if (piece.type !== PieceType.Virus) {
            if (distanceFromStart <= 3) {
              // Before middle line: +2 per square
              positionScore = distanceFromStart * 2;
              score += positionScore * multiplier;
              pieceScore += positionScore;
              if (positionScore > 0) pieceBreakdown.push(`pos(+${positionScore})`);
            } else {
              // Past middle line: +1 per square (after the +6 from reaching middle)
              const pastMiddle = distanceFromStart - 3;
              positionScore = 6 + pastMiddle;
              score += positionScore * multiplier;
              pieceScore += positionScore;
              pieceBreakdown.push(`pos(+${positionScore})`);
            }
          }

          // 5. FROG LILYPAD BONUS: +5 for frog holding lilypad ability
          // hasLilyJumped = false means lily is available, true means it's been used
          if (piece.type === PieceType.Frog) {
            if (!piece.hasLilyJumped) {
              score += 5 * multiplier; // Apply from perspective
              pieceScore += 5;
              pieceBreakdown.push(`lily(+5)`);
            }
          }

          // 6. SNAKE SHED PENALTY: -3 for snake holding shed ability
          // hasShed = false means shed is available, true means it's been used
          if (piece.type === PieceType.Snake) {
            if (!piece.hasShed) {
              score -= 3 * multiplier; // Apply from perspective
              pieceScore -= 3;
              pieceBreakdown.push(`shed(-3)`);
            }
          }

          // 7. STATUS EFFECT SCORING
          // POISONED: -2 for our poisoned pieces, +2 for enemy poisoned
          if (piece.poisonedStatus?.isPoisoned) {
            const effectScore = isOurs ? -2 : 2;
            score += effectScore;
            pieceScore += isOurs ? -2 : 0;
            if (isOurs) pieceBreakdown.push(`poisoned(-2)`);
          }
          // ENTANGLED: -5 penalty for display
          // Entangled pieces CANNOT MOVE - completely immobilized until Octopus moves/dies
          // The CommonAIUtils blocks entangled moves, so this is just for evaluation scoring
          if (piece.isEntangled) {
            const effectScore = isOurs ? -5 : 5;
            score += effectScore;
            pieceScore += isOurs ? -5 : 0;
            if (isOurs) pieceBreakdown.push(`entangled(-5)`);
          }

          // DOOM DETECTION: Piece stacked with its OWN team's Virus is effectively dead
          // Virus kills whatever it's stacked with when it leaves — negate the piece's FULL value
          // Applied after all scoring so it accounts for king value, position, abilities, etc.
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              // Negate everything scored so far for this piece, plus extra penalty
              const doomPenalty = -(pieceScore + 5);
              score += doomPenalty * multiplier;
              pieceScore += doomPenalty;
              pieceBreakdown.push(`doom(${doomPenalty})`);
            }
          }

          // Add to breakdown
          if (piece.isLight) {
            lightPieces.push({
              piece: piece.type,
              position: `[${row},${col}]`,
              breakdown: pieceBreakdown.join(' '),
              score: pieceScore,
              isKing: piece.isKing
            });
            lightTotal += pieceScore;
          } else {
            darkPieces.push({
              piece: piece.type,
              position: `[${row},${col}]`,
              breakdown: pieceBreakdown.join(' '),
              score: pieceScore,
              isKing: piece.isKing
            });
            darkTotal += pieceScore;
          }
        }
      }
    }

    // 8. KING SAFETY: Penalize exposed/threatened kings, reward pressuring enemy king
    const kingSafetyScore = this.evaluateKingSafety(gameState, perspective);
    score += kingSafetyScore;

    // Store breakdown for UI display ONLY when explicitly requested (at root)
    if (debug) {
      // Annotate king safety on the AI's king entry in breakdown
      if (kingSafetyScore !== 0) {
        const ourKingEntry = (perspective ? lightPieces : darkPieces).find(p => p.isKing);
        if (ourKingEntry) {
          ourKingEntry.breakdown += ` safety(${kingSafetyScore > 0 ? '+' : ''}${kingSafetyScore})`;
          ourKingEntry.score += kingSafetyScore;
          if (perspective) lightTotal += kingSafetyScore;
          else darkTotal += kingSafetyScore;
        }
      }

      this.lastBreakdown = {
        lightPieces,
        darkPieces,
        lightTotal,
        darkTotal,
        finalScore: lightTotal - darkTotal,
        perspective: perspective ? 'Light' : 'Dark'
      };
    }

    return score;
  }

  /**
   * Get the last evaluation breakdown for UI display
   */
  static getLastBreakdown(): EvaluationBreakdown | null {
    return this.lastBreakdown;
  }

  /**
   * Get absolute board value ALWAYS from Light's perspective (Light - Dark)
   * This is for PV display where we need consistent perspective regardless of whose turn it is
   *
   * CRITICAL: Must use IDENTICAL scoring to evaluate() to ensure consistency!
   * CRITICAL: Must check for TERMINAL STATES (game over conditions)!
   */
  static getAbsoluteBoardValue(gameState: GameState): number {
    // Check for terminal states FIRST (same as evaluate())
    if (gameState.state === State.PostGame) {
      const winScore = 100000 + (isRaceVictory(gameState) ? RACE_VICTORY_BONUS : 0);
      if (gameState.lightWon === true) {
        return winScore; // Light won
      }
      if (gameState.lightWon === false) {
        return -winScore; // Dark won
      }
    }
    if (gameState.state === State.Draw || gameState.state === State.Terminated) {
      return 0;
    }

    // Check for king victory race (king on goal row)
    let lightKingOnGoal = false;
    let darkKingOnGoal = false;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing) {
            const goalRow = piece.isLight ? 7 : 0;
            if (row === goalRow) {
              if (piece.isLight) lightKingOnGoal = true;
              else darkKingOnGoal = true;
            }
          }
        }
      }
    }

    if (lightKingOnGoal) return 100000 + RACE_VICTORY_BONUS; // Light won by race
    if (darkKingOnGoal) return -(100000 + RACE_VICTORY_BONUS); // Dark won by race

    let lightTotal = 0;
    let darkTotal = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          let pieceScore = 10; // Base piece value

          // King bonus
          if (piece.isKing) {
            pieceScore += 1000;
          }

          // FIXED: Positional scoring - MUST MATCH evaluate() logic!
          // Forward movement rewards (Light moves up, Dark moves down)
          const middleLine = 3.5;
          let distanceFromStart: number;

          if (piece.isLight) {
            distanceFromStart = row; // Light starts at row 0, advances to row 7
          } else {
            distanceFromStart = 7 - row; // Dark starts at row 7, advances to row 0
          }

          // +2 per square toward middle line, +1 per square past middle
          let positionScore = 0;
          if (piece.type !== PieceType.Virus) {
            if (distanceFromStart <= 3) {
              positionScore = distanceFromStart * 2;
            } else {
              const pastMiddle = distanceFromStart - 3;
              positionScore = 6 + pastMiddle;
            }
          }
          pieceScore += positionScore;

          // Special piece bonuses (MATCH evaluate() logic)
          if (piece.type === PieceType.Frog && !piece.hasLilyJumped) {
            pieceScore += 5;
          }
          if (piece.type === PieceType.Snake && !piece.hasShed) {
            pieceScore -= 3;
          }

          // Status effect penalties (MATCH evaluate() logic)
          if (piece.poisonedStatus?.isPoisoned) {
            pieceScore -= 2;
          }
          if (piece.isEntangled) {
            pieceScore -= 5; // FIXED: was -2, should be -5 to match evaluate()
          }

          // DOOM: Piece stacked with own Virus is effectively dead (MATCH evaluate() logic)
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceScore = -(pieceScore + 5); // Negate full value + penalty
            }
          }

          // Add to appropriate total
          if (piece.isLight) {
            lightTotal += pieceScore;
          } else {
            darkTotal += pieceScore;
          }
        }
      }
    }

    // King safety from Light's perspective
    const kingSafety = this.evaluateKingSafety(gameState, true);

    return lightTotal - darkTotal + kingSafety;
  }

  /**
   * Get approximate attack range for a piece type (Manhattan distance).
   * Penguin capped at 3 (slides are blockable; full range would over-penalize).
   */
  private static getAttackRange(pieceType: PieceType): number {
    switch (pieceType) {
      case PieceType.Snake: return 2;
      case PieceType.Crane: return 2;
      case PieceType.Frog: return 2;
      case PieceType.Rhino: return 3;
      case PieceType.Penguin: return 3;
      case PieceType.Virus: return 2;
      default: return 1; // Bear, Fungi, Goat, Monkey, Octopus, Tree, Wolf
    }
  }

  /**
   * Evaluate king safety for BOTH sides.
   * Returns score from perspective's viewpoint (positive = safe for us / enemy unsafe).
   *
   * Scans all enemy pieces, applies Manhattan distance penalties based on attack range.
   * Even at depth 1, prevents the AI from walking its king into capture range.
   */
  private static evaluateKingSafety(gameState: GameState, perspective: boolean): number {
    let score = 0;

    // Collect pieces and find both kings
    const allPieces: Array<{ piece: IPiece; row: number; col: number }> = [];
    let ourKingRow = -1, ourKingCol = -1;
    let enemyKingRow = -1, enemyKingCol = -1;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        for (const piece of gameState.board[row][col].Pieces) {
          allPieces.push({ piece, row, col });
          if (piece.isKing) {
            if (piece.isLight === perspective) {
              ourKingRow = row; ourKingCol = col;
            } else {
              enemyKingRow = row; enemyKingCol = col;
            }
          }
        }
      }
    }

    // Evaluate OUR king's safety
    if (ourKingRow !== -1) {
      let ourSafety = 0;
      let friendlyAdjacent = 0;

      for (const { piece, row, col } of allPieces) {
        if (piece.isLight === perspective) {
          // Count friendly non-king pieces adjacent to our king
          if (!piece.isKing) {
            const dist = Math.abs(row - ourKingRow) + Math.abs(col - ourKingCol);
            if (dist === 1) friendlyAdjacent++;
          }
          continue;
        }
        // Enemy piece — threat check
        if (piece.isEntangled) continue;
        const dist = Math.abs(row - ourKingRow) + Math.abs(col - ourKingCol);
        const attackRange = this.getAttackRange(piece.type);
        if (dist <= attackRange) {
          ourSafety -= (attackRange - dist + 1) * 12;
          // Bear adjacent is especially lethal (Savage overrides all defenses)
          if (piece.type === PieceType.Bear && dist <= 1) {
            ourSafety -= 25;
          }
        }
      }

      // Lonely king rushing ahead penalty
      const kingAdvancement = perspective ? ourKingRow : (7 - ourKingRow);
      if (friendlyAdjacent === 0 && kingAdvancement >= 3) {
        ourSafety -= 15;
      }

      // Edge column bonus (fewer attack angles on 5-col board)
      if (ourKingCol === 0 || ourKingCol === 4) {
        ourSafety += 3;
      }

      score += ourSafety;
    }

    // Evaluate ENEMY king's safety (their unsafety = good for us)
    if (enemyKingRow !== -1) {
      let enemySafety = 0;
      let enemyFriendlyAdjacent = 0;

      for (const { piece, row, col } of allPieces) {
        if (piece.isLight !== perspective) {
          // Enemy's friendly piece — check adjacency to enemy king
          if (!piece.isKing) {
            const dist = Math.abs(row - enemyKingRow) + Math.abs(col - enemyKingCol);
            if (dist === 1) enemyFriendlyAdjacent++;
          }
          continue;
        }
        // Our piece threatening enemy king
        if (piece.isEntangled) continue;
        const dist = Math.abs(row - enemyKingRow) + Math.abs(col - enemyKingCol);
        const attackRange = this.getAttackRange(piece.type);
        if (dist <= attackRange) {
          enemySafety -= (attackRange - dist + 1) * 12;
          if (piece.type === PieceType.Bear && dist <= 1) {
            enemySafety -= 25;
          }
        }
      }

      const enemyAdvancement = perspective ? (7 - enemyKingRow) : enemyKingRow;
      if (enemyFriendlyAdjacent === 0 && enemyAdvancement >= 3) {
        enemySafety -= 15;
      }
      if (enemyKingCol === 0 || enemyKingCol === 4) {
        enemySafety += 3;
      }

      // Enemy's unsafety is GOOD for us (negate their safety)
      score -= enemySafety;
    }

    return score;
  }

  static quickEvaluate(gameState: GameState, isMaximizing: boolean): number {
    const perspective = gameState.isLightTurn;
    let score = 0;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          const multiplier = (piece.isLight === perspective) ? 1 : -1;
          let pieceValue = 10;
          if (piece.isKing) {
            pieceValue += 1000;
          }
          // DOOM: Piece stacked with own Virus is effectively dead
          if (piece.type !== PieceType.Virus && pieces.length > 1) {
            const friendlyVirus = pieces.find(p =>
              p.type === PieceType.Virus && p.isLight === piece.isLight && p !== piece
            );
            if (friendlyVirus) {
              pieceValue = -(pieceValue + 5);
            }
          }
          score += pieceValue * multiplier;
        }
      }
    }

    return score;
  }
}
